import { useState } from 'react';
import { getStoredToken } from '@/lib/api';
import { PasteableUpload } from '@/components/common/PasteableUpload';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';

export interface PhotoSlotProps {
  tripId: string;
  slot: 'pickup-truck' | 'pickup-receipt' | 'wasika' | 'delivery-truck' | 'delivery-document';
  label: string;
  required?: boolean;
  filledUrl?: string | null;
  uploadedAt?: string | null;
  uploadedByName?: string | null;
  accept?: string;
  onChange: () => void;
  highlight?: boolean;
}

/**
 * PR-UPLOAD-FIX MAY 11 — flatbed Documents tab slot. Thin wrapper over
 * PasteableUpload that knows the slot's upload + view URLs and which mime
 * to advertise (wasika = PDF, everything else = image). Inherits the new
 * paste / drag-drop / stage-then-commit behavior for free.
 */
export function PhotoSlot({
  tripId, slot, label, required, filledUrl, uploadedAt, uploadedByName,
  accept, onChange, highlight,
}: PhotoSlotProps) {
  const [replacing, setReplacing] = useState(false);

  const isPdf = slot === 'wasika';
  const effectiveAccept = accept ?? (isPdf ? 'application/pdf' : 'image/*');
  const viewUrl = `/api/v1/flatbed/trips/${tripId}/photos/${slot}/file`;

  async function upload(file: File): Promise<void> {
    const fd = new FormData();
    fd.append('file', file);
    const jwt = getStoredToken();
    const res = await fetch(`${API_BASE}/api/v1/flatbed/trips/${tripId}/photos/${slot}`, {
      method: 'POST',
      headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      body: fd,
    });
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const j = await res.json();
        if (j?.message || j?.error) detail = j.message ?? j.error;
      } catch { /* ignore */ }
      throw new Error('Upload failed: ' + detail);
    }
    setReplacing(false);
    onChange();
  }

  const filled = !!filledUrl && !replacing;

  return (
    <div className="space-y-1">
      <PasteableUpload
        label={
          <span className="text-sm font-medium inline-flex items-center gap-1">
            {label}
            {required && !filled && <span className="text-rose-600 dark:text-rose-400" title="Required">⚠️</span>}
            {required && filled && <span className="text-emerald-600 dark:text-emerald-400" title="Filled">✓</span>}
          </span>
        }
        helperText={isPdf ? 'PDF · click, drop, or paste' : 'Image · click, drop, or paste (Cmd+V)'}
        accept={effectiveAccept}
        maxSizeMB={25}
        currentFileUrl={filled ? viewUrl : null}
        currentFileType={filled ? (isPdf ? 'application/pdf' : null) : null}
        onReplace={() => setReplacing(true)}
        onUpload={upload}
        highlight={highlight && !filled}
      />
      {(uploadedAt || uploadedByName) && filled && (
        <div className="text-[11px] text-muted-foreground px-1">
          {uploadedAt ? new Date(uploadedAt).toLocaleString('en-GB') : '—'}
          {uploadedByName ? ' · ' + uploadedByName : ''}
        </div>
      )}
    </div>
  );
}

